import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowRight, BarChart3, Users, Target, TrendingUp, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_research-hub-218/artifacts/lk2wm99x_Skoop%21%20Logo%20-%202026.jpg";
const HERO_IMG = "https://images.unsplash.com/photo-1660945100284-2e6687e71986?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHxjb2xvcmFkbyUyMG1vdW50YWlucyUyMHN1bW1lcnxlbnwwfHx8fDE3NzYyOTYwMjN8MA&ixlib=rb-4.1.0&q=85&w=1920";

export default function HomePage() {
  const [formData, setFormData] = useState({ first_name: '', email: '', zip_code: '' });
  const [submitStatus, setSubmitStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (window.location.hash === '#signup') {
      setTimeout(() => {
        const el = document.getElementById('signup');
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    }
  }, []);

  const handleSignup = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitStatus(null);
    try {
      await axios.post(`${API}/survey-signups`, formData);
      setSubmitStatus({ type: 'success', message: "You're signed up! We'll be in touch." });
      setFormData({ first_name: '', email: '', zip_code: '' });
    } catch (err) {
      const msg = err.response?.data?.detail || 'Something went wrong. Please try again.';
      setSubmitStatus({ type: 'error', message: typeof msg === 'string' ? msg : JSON.stringify(msg) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Colorado Mountains" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#0A192F]/70" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 lg:py-40">
          <div className="flex flex-col items-center text-center">
            <img src={LOGO_URL} alt="SKOOP! Logo" className="h-32 sm:h-40 w-auto mb-8 animate-fade-up p-2 bg-white/10 rounded-2xl backdrop-blur-sm" style={{ filter: 'drop-shadow(0 0 40px rgba(10,25,47,0.6))' }} />
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white mb-6 animate-fade-up animate-delay-100" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Welcome to SKOOP!
            </h1>
            <p className="text-lg sm:text-xl text-white/80 max-w-2xl mb-10 animate-fade-up animate-delay-200" style={{ fontFamily: 'Manrope, sans-serif' }}>
              SKOOP! keeps the community's pulse on the community through routine studies on topics that matter.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-up animate-delay-300">
              <div className="relative group">
                <a
                  href="#signup"
                  data-testid="hero-signup-btn"
                  className="neo-btn px-8 py-4 bg-[#38BDF8] text-[#0A192F] text-base inline-flex items-center gap-2 justify-center"
                >
                  Join SKOOP! <ArrowRight size={18} />
                </a>
                <span className="absolute left-1/2 -translate-x-1/2 top-full mt-3 px-4 py-2 bg-[#0A192F] text-white text-xs font-semibold rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none shadow-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Sign up to receive our surveys and newsletters!
                </span>
              </div>
              <div className="relative group">
                <a
                  href="/get-the-skoop"
                  data-testid="hero-services-btn"
                  className="neo-btn px-8 py-4 bg-white text-[#0A192F] text-base inline-flex items-center gap-2 justify-center"
                >
                  Get the SKOOP!
                </a>
                <span className="absolute left-1/2 -translate-x-1/2 top-full mt-3 px-4 py-2 bg-[#0A192F] text-white text-xs font-semibold rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none shadow-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Sign up to receive the results from our studies
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="py-20 sm:py-28 bg-[#F8FAFC]" data-testid="what-we-do-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-16">
            <span className="neo-btn inline-block px-4 py-1.5 bg-[#38BDF8] text-[#0A192F] text-xs font-bold uppercase tracking-widest mb-6">
              What We Do
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0A192F] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              What We Do and How We Do It
            </h2>
            <p className="text-base sm:text-lg text-[#475569] leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif' }}>
              We are a Marketing Research Firm that provides logical advice to clients through obtaining the best, most reliable information. The information we provide then enables our clients to make effective management, product development, and/or investment decisions.
            </p>
            <p className="text-base sm:text-lg text-[#475569] leading-relaxed mt-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Our goal is to define your target audience by understanding how emotions and environments influence decisions they make. Understanding these influences opens the gateway for increased satisfaction and increase profitability.
            </p>
          </div>

          {/* Feature grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: <BarChart3 size={28} />, title: 'Data-Driven Insights', desc: 'We capture reliable data to help you make informed decisions.' },
              { icon: <Users size={28} />, title: 'Target Audience', desc: 'Define and understand the people who matter most to your success.' },
              { icon: <Target size={28} />, title: 'Emotional Intelligence', desc: 'Understanding how emotions influence decisions and purchases.' },
              { icon: <TrendingUp size={28} />, title: 'Growth Strategy', desc: 'Opening the gateway for increased satisfaction and profitability.' },
            ].map((item, i) => (
              <div key={i} className={`neo-card p-8 animate-fade-up animate-delay-${(i+1)*100}`}>
                <div className="w-14 h-14 rounded-xl bg-[#0A192F] text-[#38BDF8] flex items-center justify-center mb-5">
                  {item.icon}
                </div>
                <h3 className="text-lg font-bold text-[#0A192F] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{item.title}</h3>
                <p className="text-sm text-[#475569] leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sign-up Section */}
      <section id="signup" className="py-20 sm:py-28 bg-[#0A192F]" data-testid="signup-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block px-4 py-1.5 rounded-full bg-[#38BDF8] text-[#0A192F] text-xs font-bold uppercase tracking-widest mb-6">
                Join SKOOP!
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Sign Up to Receive Surveys &amp; Communications
              </h2>
              <p className="text-base text-white/70 leading-relaxed mb-8" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Be a voice in your community. Sign up to participate in research studies on topics that impact our community. Your feedback helps shape better decisions.
              </p>
              <div className="space-y-3">
                {['Quick community surveys', 'Topics that matter locally', 'Your voice makes a difference'].map((t, i) => (
                  <div key={i} className="flex items-center gap-3 text-white/80 text-sm">
                    <CheckCircle size={18} className="text-[#38BDF8] shrink-0" />
                    <span>{t}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="neo-card p-8 sm:p-10 bg-white">
              <h3 className="text-xl font-bold text-[#0A192F] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Get Started
              </h3>
              <form onSubmit={handleSignup} className="space-y-5" data-testid="signup-form">
                <div>
                  <Label htmlFor="first_name" className="text-sm font-semibold text-[#0A192F] mb-1.5 block">First Name</Label>
                  <Input
                    id="first_name"
                    data-testid="signup-first-name"
                    className="neo-input h-12"
                    placeholder="Enter your first name"
                    value={formData.first_name}
                    onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-sm font-semibold text-[#0A192F] mb-1.5 block">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="signup-email"
                    className="neo-input h-12"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="zip_code" className="text-sm font-semibold text-[#0A192F] mb-1.5 block">Zip Code</Label>
                  <Input
                    id="zip_code"
                    data-testid="signup-zip-code"
                    className="neo-input h-12"
                    placeholder="e.g. 81620"
                    value={formData.zip_code}
                    onChange={(e) => setFormData({...formData, zip_code: e.target.value})}
                    required
                  />
                </div>
                {submitStatus && (
                  <div
                    data-testid="signup-status"
                    className={`p-4 rounded-lg text-sm font-medium ${
                      submitStatus.type === 'success'
                        ? 'bg-[#166534]/10 text-[#166534] border-2 border-[#166534]'
                        : 'bg-[#DC2626]/10 text-[#DC2626] border-2 border-[#DC2626]'
                    }`}
                  >
                    {submitStatus.message}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="signup-submit-btn"
                  className="neo-btn w-full py-3.5 bg-[#38BDF8] text-[#0A192F] text-base disabled:opacity-50"
                >
                  {submitting ? 'Signing Up...' : 'Sign Up Now'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
